import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import { Selection } from "../selection/Selection";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

// Freehand Lasso. Photoshop shortcut L (cycle through lasso variants).
//
// Behaviors:
//   - Pointer down anchors the start of a freehand path.
//   - Pointer move appends a new vertex if the pointer has moved at least
//     1 doc-pixel from the previous vertex (sub-pixel jitter is dropped).
//   - During the drag, a transient SetSelectionCommand preview is emitted
//     every few moves so the user sees the in-progress selection. We trigger
//     a preview every 4th move OR when the polygon hits a multiple-of-8 size,
//     whichever comes first.
//   - On pointer up, the polygon is closed (last vertex → first), rasterized
//     via Selection.fromPolygon, combined against the existing selection per
//     the pointer-down modifier state, and emitted as a real (undoable)
//     SetSelectionCommand.
//   - cancel() restores the original selection transiently.
//
// Modifier conventions match MarqueeTool (evaluated from pointer-down):
//   default            → replace
//   shift only         → add (union)
//   alt only           → subtract
//   shift + alt        → intersect
//
// No DOM access — Escape handling is wired by the host via cancel().

export type CombineMode = "replace" | "add" | "subtract" | "intersect";

export interface LassoToolOptions {
  readonly id: string;
  readonly shortcut: string;
}

interface DocPoint {
  x: number;
  y: number;
}

interface Drag {
  readonly originalSelection: Selection | null;
  readonly combine: CombineMode;
  readonly docW: number;
  readonly docH: number;
  readonly points: DocPoint[];
  movesSincePreview: number;
}

export class LassoTool implements Tool {
  readonly id: string;
  readonly shortcut: string;

  /** Feather radius in doc pixels, applied to the committed selection (audit i67). */
  feather = 0;
  /** Whether to anti-alias the selection edges (audit i159). */
  antiAlias = true;
  /**
   * Explicit combine-mode override set by the options bar's New/Add/
   * Subtract/Intersect button strip (ToolUX's wireCombineModeButtons,
   * audit i60/i162). When set to "add"/"subtract"/"intersect" it wins over
   * the shift/alt-derived default computed at pointer-down; "new" (or
   * unset) falls back to the existing shift/alt modifier behavior.
   */
  combineMode?: "new" | "add" | "subtract" | "intersect";

  private drag: Drag | null = null;

  constructor(opts: LassoToolOptions = { id: "lasso", shortcut: "L" }) {
    this.id = opts.id;
    this.shortcut = opts.shortcut;
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.drag = {
      originalSelection: ctx.doc.selection,
      combine: pickCombine(s.shiftKey, s.altKey, this.combineMode),
      docW: ctx.doc.width,
      docH: ctx.doc.height,
      points: [{ x: p.x, y: p.y }],
      movesSincePreview: 0,
    };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.drag) return;
    const d = this.drag;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const last = d.points[d.points.length - 1]!;
    // Sub-pixel subsampling: skip if we haven't moved a full doc-pixel.
    const dx = p.x - last.x;
    const dy = p.y - last.y;
    if (dx * dx + dy * dy < 1) return;
    d.points.push({ x: p.x, y: p.y });
    d.movesSincePreview++;
    // Preview cadence: every 4 accepted moves, OR whenever the polygon size
    // hits a multiple of 8 — whichever comes first.
    if (d.movesSincePreview >= 4 || d.points.length % 8 === 0) {
      d.movesSincePreview = 0;
      const preview = this.buildSelection(d);
      ctx.emitTransient(new SetSelectionCommand(preview));
    }
  }

  onPointerUp(ctx: ToolContext, _s: PointerSample): void {
    if (!this.drag) return;
    const d = this.drag;
    this.drag = null;
    // If the user never produced enough vertices for a polygon, treat as a
    // stray click — restore the original selection if we previously emitted
    // any transient preview, otherwise do nothing.
    if (d.points.length < 3) {
      // We may have emitted transient previews from a tiny drag, so we still
      // need to anchor the final state. Only emit if anything was previewed.
      // (We can't easily know — emit a final replace with original if we did
      //  preview at all.) The simplest correct behavior is: emit a final
      // command equal to the original selection so any preview is reverted.
      ctx.emit(new SetSelectionCommand(d.originalSelection));
      return;
    }
    const next = this.buildSelection(d);
    // Apply feather at commit time (matches MarqueeTool's convention).
    const feathered = next && this.feather > 0 ? next.feather(this.feather) : next;
    ctx.emit(new SetSelectionCommand(feathered));
  }

  /** Return true when a pointer drag is in progress (for host Space/Escape routing). */
  isDragging(): boolean {
    return this.drag !== null;
  }

  /**
   * Cancel the in-progress lasso (call from Escape handler in the host). The
   * original selection is re-emitted transiently so any live preview reverts.
   */
  cancel(ctx: ToolContext): void {
    if (!this.drag) return;
    const original = this.drag.originalSelection;
    this.drag = null;
    ctx.emitTransient(new SetSelectionCommand(original));
  }

  private buildSelection(d: Drag): Selection | null {
    const local = Selection.fromPolygon(d.docW, d.docH, d.points, this.antiAlias);
    const original = d.originalSelection;
    switch (d.combine) {
      case "replace":
        return local.isEmpty() ? null : local;
      case "add":
        if (!original) return local.isEmpty() ? null : local;
        if (local.isEmpty()) return original;
        return original.union(local);
      case "subtract":
        if (!original) return null;
        if (local.isEmpty()) return original;
        return original.subtract(local);
      case "intersect":
        if (!original) return null;
        if (local.isEmpty()) return null;
        return original.intersect(local);
    }
  }
}

function pickCombine(shift: boolean, alt: boolean, override?: string): CombineMode {
  // An explicit options-bar combine-mode selection (audit i60/i162) wins over
  // the shift/alt-derived default. "new" (or no override) falls through to
  // the existing modifier-key behavior below.
  if (override === "add" || override === "subtract" || override === "intersect") {
    return override;
  }
  if (shift && alt) return "intersect";
  if (shift) return "add";
  if (alt) return "subtract";
  return "replace";
}
